import { supabase } from '../../lib/supabase'

import { QuotaExhaustedApiError } from '../billing/api'
import { getDeviceFingerprint } from '../risk/deviceFingerprint'
import { JdPrecheckBlockedError } from './optimizationJdPrecheck'

import { allBlocksTerminal, runBlocksFingerprint } from './blockProgress'
import {
  buildInitialSectionBlocks,
  companyNamesFromWorkExperience,
  mergeWorkExperienceBlocks,
} from './blockIds'
import {
  buildSectionFingerprints,
  planIncrementalBlocks,
} from './incrementalPlan'
import { DEFAULT_MODEL_STACK, isModelStackId, type ModelStackId } from './modelStacks'
import type { OptimizationQualityEval } from './qualityEval'
import type { OptimizationBlockState, ResumeOptimizationRun } from './types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

const POLL_MS_ACTIVE = 800
const POLL_MS_FINALIZING = 2500

function mapRun(row: Record<string, unknown>): ResumeOptimizationRun {
  const modelStackRaw = String(row.model_stack || DEFAULT_MODEL_STACK)
  const model_stack = isModelStackId(modelStackRaw) ? modelStackRaw : DEFAULT_MODEL_STACK

  return {
    id: String(row.id),
    user_id: String(row.user_id),
    resume_id: String(row.resume_id),
    job_description: String(row.job_description || ''),
    model_stack,
    status: row.status as ResumeOptimizationRun['status'],
    blocks: Array.isArray(row.blocks) ? (row.blocks as OptimizationBlockState[]) : [],
    quality_eval: (row.quality_eval as OptimizationQualityEval | null) ?? null,
    incremental_snapshot:
      (row.incremental_snapshot as ResumeOptimizationRun['incremental_snapshot']) ?? null,
    feedback_json: (row.feedback_json as Record<string, unknown> | null) ?? null,
    error_message: row.error_message != null ? String(row.error_message) : null,
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
  }
}

async function fetchResumeSectionRows(
  resumeId: string
): Promise<Array<{ section_type: string; content: unknown }>> {
  const { data, error } = await supabase
    .from('resume_content')
    .select('section_type, content')
    .eq('resume_id', resumeId)

  if (error) throw new Error(error.message)
  if (!Array.isArray(data)) return []
  return data
    .filter((row): row is { section_type: string; content: unknown } => {
      return Boolean(row && typeof row === 'object' && typeof row.section_type === 'string')
    })
    .map((row) => ({ section_type: row.section_type, content: row.content }))
}

async function fetchLastSuccessfulRun(resumeId: string): Promise<ResumeOptimizationRun | null> {
  const { data, error } = await supabase
    .from('resume_optimization_runs')
    .select('*')
    .eq('resume_id', resumeId)
    .eq('status', 'completed')
    .order('created_at', { ascending: false })
    .limit(8)

  if (error) throw new Error(error.message)
  if (!Array.isArray(data)) return null

  for (const row of data) {
    const run = mapRun(row as Record<string, unknown>)
    if (run.quality_eval?.primary_pass) return run
  }
  return null
}

export async function createOptimizationRun(params: {
  resumeId: string
  jobDescription: string
  workExperience: unknown
  modelStack: ModelStackId
}): Promise<ResumeOptimizationRun> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser()
  if (userError || !user) {
    throw new Error('Sign in required to optimize resume.')
  }

  const sectionBlocks = buildInitialSectionBlocks()
  const companies = companyNamesFromWorkExperience(params.workExperience)
  const baseBlocks = mergeWorkExperienceBlocks(sectionBlocks, companies)

  const [sectionRows, previousRun] = await Promise.all([
    fetchResumeSectionRows(params.resumeId),
    fetchLastSuccessfulRun(params.resumeId),
  ])
  const sectionFingerprints = buildSectionFingerprints(sectionRows, params.workExperience)
  const { blocks } = planIncrementalBlocks({
    blocks: baseBlocks,
    jobDescription: params.jobDescription.trim(),
    previousRun,
    sectionFingerprints,
  })

  const { data, error } = await supabase
    .from('resume_optimization_runs')
    .insert({
      user_id: user.id,
      resume_id: params.resumeId,
      job_description: params.jobDescription.trim(),
      model_stack: params.modelStack,
      status: 'pending',
      blocks,
    })
    .select('*')
    .single()

  if (error || !data) {
    throw new Error(error?.message || 'Failed to create optimization run.')
  }

  return mapRun(data as Record<string, unknown>)
}

export async function fetchOptimizationRun(runId: string): Promise<ResumeOptimizationRun | null> {
  const { data, error } = await supabase
    .from('resume_optimization_runs')
    .select('*')
    .eq('id', runId)
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) return null
  return mapRun(data as Record<string, unknown>)
}

export async function startOptimizationRunOnApi(runId: string): Promise<void> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  const deviceFingerprint = await getDeviceFingerprint()
  if (deviceFingerprint) {
    headers['X-Applicator-Device-Id'] = deviceFingerprint
  }

  const response = await fetch(`${API_BASE_URL}/api/v1/optimization/runs/${runId}/start`, {
    method: 'POST',
    headers,
  })

  if (!response.ok) {
    if (response.status === 402) {
      let payload: Record<string, unknown> = {}
      try {
        payload = (await response.json()) as Record<string, unknown>
      } catch {
        payload = {}
      }
      throw new QuotaExhaustedApiError({
        message: String(payload.message || 'No optimizations remaining'),
        reason: payload.reason as string | null | undefined,
        duplicate_layer: payload.duplicate_layer as string | null | undefined,
        tier: payload.tier as string | undefined,
        remaining: Number(payload.remaining ?? 0),
        cap: Number(payload.cap ?? 0),
      })
    }
    if (response.status === 422) {
      let payload: Record<string, unknown> = {}
      try {
        const body = (await response.json()) as { detail?: Record<string, unknown> }
        payload = (body.detail as Record<string, unknown>) || body
      } catch {
        payload = {}
      }
      if (payload.error === 'jd_high_risk' || payload.blocked === true) {
        throw new JdPrecheckBlockedError({
          user_message: String(payload.user_message || ''),
          support_email: String(payload.support_email || ''),
          risk_tier: String(payload.risk_tier || 'high'),
          risk_score: Number(payload.risk_score ?? 1),
        })
      }
    }
    const text = await response.text()
    throw new Error(text || `Optimization start failed (${response.status})`)
  }
}

export function subscribeOptimizationRun(
  runId: string,
  onUpdate: (run: ResumeOptimizationRun) => void,
  onBlocksProgress?: (run: ResumeOptimizationRun) => void
): () => void {
  let cancelled = false
  let timer: ReturnType<typeof setInterval> | null = null
  let lastDoneCount = 0
  let lastFingerprint = ''
  let pollIntervalMs = POLL_MS_ACTIVE

  const clearPollTimer = () => {
    if (timer) {
      clearInterval(timer)
      timer = null
    }
  }

  const schedulePoll = () => {
    clearPollTimer()
    timer = setInterval(() => {
      void poll()
    }, pollIntervalMs)
  }

  const poll = async () => {
    if (cancelled) return
    try {
      const run = await fetchOptimizationRun(runId)
      if (!run) return

      const fingerprint = runBlocksFingerprint(run)
      const fingerprintChanged = fingerprint !== lastFingerprint
      const blocks = run.blocks ?? []
      const doneCount = blocks.filter(
        (b) => b.status === 'done' || b.status === 'skipped'
      ).length
      const terminal = allBlocksTerminal(blocks)
      const nextInterval = terminal && run.status === 'running' ? POLL_MS_FINALIZING : POLL_MS_ACTIVE

      if (nextInterval !== pollIntervalMs) {
        pollIntervalMs = nextInterval
        schedulePoll()
      }

      if (doneCount > lastDoneCount) {
        lastDoneCount = doneCount
        onBlocksProgress?.(run)
      }

      const isTerminalRun = run.status === 'completed' || run.status === 'failed'
      if (fingerprintChanged || isTerminalRun) {
        lastFingerprint = fingerprint
        onUpdate(run)
      }

      if (isTerminalRun) {
        clearPollTimer()
      }
    } catch {
      // keep polling
    }
  }

  void poll()
  schedulePoll()

  return () => {
    cancelled = true
    clearPollTimer()
  }
}

export function blockStateForId(
  blocks: OptimizationBlockState[],
  blockId: string
): OptimizationBlockState | undefined {
  return blocks.find((b) => b.block_id === blockId)
}

export function blockStateForSection(
  blocks: OptimizationBlockState[],
  sectionType: string,
  companyName?: string
): OptimizationBlockState | undefined {
  if (sectionType === 'work_experience' && companyName) {
    return blocks.find((b) => b.company_name === companyName)
  }
  if (sectionType === 'certifications' || sectionType === 'interests') {
    return blocks.find((b) => b.block_id === 'section:micro_sections')
  }
  return blocks.find((b) => b.section_type === sectionType && !b.company_name)
}
